import type {Textile, TextileSearchResult} from "#material-outputs-publishing/targets/configura/client"

/**
 * The guards standing between an article-ID search and an irreversible mis-bind (the Material Database has no
 * DELETE). Pure functions over the search result so they are testable without pothos and move
 * cheaply if binding ever moves out of the trigger mutation.
 */

export type BindingGuardFailure =
    | {kind: "noMatch"; sku: string}
    | {kind: "multipleMatches"; sku: string; total: number}
    | {kind: "skuMismatch"; sku: string; returnedSku: string}
    | {kind: "companyMismatch"; sku: string; expectedCompanyId: string; actualCompanyId: string}
    | {kind: "brandMismatch"; sku: string; expectedBrand: string; actualBrand: string | null}

export type BindingCandidateResult = {ok: true; textile: Textile} | {ok: false; failure: BindingGuardFailure}

/**
 * `expectedBrand` is the organization's brand, or `null` when it has none. Required rather than
 * optional so that no caller drops the brand guard by omission.
 */
export function checkBindingCandidate(
    search: TextileSearchResult,
    sku: string,
    expectedCompanyId: string,
    expectedBrand: string | null,
): BindingCandidateResult {
    // Branch on `total`, never on `results.length` — `results` is one page, `total` is the match
    // count. With a paged search and colliding skus, `results.length` can be 1 while total is not.
    if (search.total === 0) {
        return {ok: false, failure: {kind: "noMatch", sku}}
    }
    if (search.total > 1) {
        return {ok: false, failure: {kind: "multipleMatches", sku, total: search.total}}
    }
    const textile = search.results[0]
    if (!textile) {
        // total said 1 but the page is empty — treat as no match rather than trusting the count.
        return {ok: false, failure: {kind: "noMatch", sku}}
    }
    // Guards against non-exact index matching, now or after a future re-analysis of their field.
    if (textile.sku !== sku) {
        return {ok: false, failure: {kind: "skuMismatch", sku, returnedSku: textile.sku}}
    }
    // A same-sku match in another company would be a silent, irreversible cross-tenant write. The
    // search itself is company-scoped, so this firing also means the Material Database ignored the filter
    // clause — which is why the re-check stays despite the scoping.
    if (textile.company.id !== expectedCompanyId) {
        return {ok: false, failure: {kind: "companyMismatch", sku, expectedCompanyId, actualCompanyId: textile.company.id}}
    }
    // A key reaches every brand of its company, so only the brand keeps one organization off another's
    // textiles. Exact and case-sensitive: a wrong bind is permanent, a refusal is not.
    if (expectedBrand !== null && textile.brand !== expectedBrand) {
        return {ok: false, failure: {kind: "brandMismatch", sku, expectedBrand, actualBrand: textile.brand ?? null}}
    }
    return {ok: true, textile}
}

export type MultiSkuSearchFailure = {kind: "pageOverflow"; total: number; received: number} | {kind: "unrequestedSku"; returnedSku: string}

export type MultiSkuSearchSplit = {ok: true; searchBySku: (sku: string) => TextileSearchResult} | {ok: false; failure: MultiSkuSearchFailure}

/**
 * Recovers per-sku search envelopes from one multi-sku search, so `checkBindingCandidate` runs
 * unchanged over each group — the group's size stands in for `total`.
 *
 * That stand-in is only sound while the page holds the entire result set, so a `total` beyond the
 * rows received fails the whole split rather than binding from a truncated page — the same
 * branch-on-`total` discipline as the single-sku path, applied one level up. A returned sku nobody
 * asked for also fails it: that can only mean the filter was ignored, and then no group can be
 * trusted.
 */
export function splitMultiSkuSearch(search: TextileSearchResult, skus: readonly string[]): MultiSkuSearchSplit {
    if (search.total > search.results.length) {
        return {ok: false, failure: {kind: "pageOverflow", total: search.total, received: search.results.length}}
    }
    const requested = new Set(skus)
    const resultsBySku = new Map<string, Textile[]>()
    for (const textile of search.results) {
        if (!requested.has(textile.sku)) {
            return {ok: false, failure: {kind: "unrequestedSku", returnedSku: textile.sku}}
        }
        const group = resultsBySku.get(textile.sku)
        if (group) {
            group.push(textile)
        } else {
            resultsBySku.set(textile.sku, [textile])
        }
    }
    return {
        ok: true,
        searchBySku: (sku) => {
            const results = resultsBySku.get(sku) ?? []
            return {total: results.length, size: search.size, from: 0, results}
        },
    }
}

export function describeMultiSkuSearchFailure(failure: MultiSkuSearchFailure): string {
    switch (failure.kind) {
        case "pageOverflow":
            return `The Material Database reported ${failure.total} matches but returned only ${failure.received} — the result set exceeds one page, so per-sku matching cannot be trusted. Retry with fewer materials.`
        case "unrequestedSku":
            return `The Material Database returned sku "${failure.returnedSku}", which was not searched for — the filter appears to have been ignored, so no match can be trusted.`
    }
}

export function describeBindingGuardFailure(failure: BindingGuardFailure): string {
    switch (failure.kind) {
        case "noMatch":
            // States the fact and not its cause, deliberately: this kind arises both where creation
            // is forbidden by the organization's profile and where a create came back `409` and the
            // re-search found nothing. Whichever it was is known only to the caller, which appends
            // it — see `describeRefusal` in bind.ts.
            return `Your company has no Configura textile with sku "${failure.sku}".`
        case "multipleMatches":
            return `${failure.total} Configura textiles share sku "${failure.sku}", so the binding would be ambiguous — likely colourways sharing an article ID.`
        case "skuMismatch":
            return `The Material Database returned sku "${failure.returnedSku}" for a search on "${failure.sku}" — refusing the non-exact match.`
        case "companyMismatch":
            return `The textile with sku "${failure.sku}" belongs to company ${failure.actualCompanyId}, not the expected company ${failure.expectedCompanyId} — binding would be a cross-tenant write.`
        case "brandMismatch":
            return failure.actualBrand === null
                ? `The textile with sku "${failure.sku}" has no brand, not this organization's brand "${failure.expectedBrand}".`
                : `The textile with sku "${failure.sku}" carries brand "${failure.actualBrand}", not this organization's brand "${failure.expectedBrand}".`
    }
}
